/**
 * File Tagging Service
 * Manages tags for files stored in localStorage
 * Tags are stored per user to keep them private
 */

export interface FileTag {
  fileName: string;
  tags: string[];
  lastModified: string;
}

export interface TagsData {
  [userId: string]: {
    [fileName: string]: string[];
  };
}

const TAGS_STORAGE_KEY = 'myportal-file-tags';

/**
 * Get all tags for a specific user
 */
export function getUserTags(userId: string): { [fileName: string]: string[] } {
  try {
    const allTags = localStorage.getItem(TAGS_STORAGE_KEY);
    if (!allTags) return {};
    
    const tagsData: TagsData = JSON.parse(allTags);
    return tagsData[userId] || {};
  } catch (error) {
    console.error('Error reading tags:', error);
    return {};
  }
}

/**
 * Get tags for a specific file
 */
export function getFileTags(userId: string, fileName: string): string[] {
  const userTags = getUserTags(userId);
  return userTags[fileName] || [];
}

/**
 * Set tags for a specific file
 */
export function setFileTags(userId: string, fileName: string, tags: string[]): void {
  try {
    const allTags = localStorage.getItem(TAGS_STORAGE_KEY);
    const tagsData: TagsData = allTags ? JSON.parse(allTags) : {};
    
    if (!tagsData[userId]) {
      tagsData[userId] = {};
    }
    
    // Clean and deduplicate tags
    const cleanTags = tags
      .map(tag => tag.trim().toLowerCase())
      .filter(tag => tag.length > 0)
      .filter((tag, index, self) => self.indexOf(tag) === index);
    
    if (cleanTags.length > 0) {
      tagsData[userId][fileName] = cleanTags;
    } else {
      // Remove entry if no tags
      delete tagsData[userId][fileName];
    }
    
    localStorage.setItem(TAGS_STORAGE_KEY, JSON.stringify(tagsData));
  } catch (error) {
    console.error('Error saving tags:', error);
  }
}

/**
 * Add tags to a file (doesn't remove existing tags)
 */
export function addFileTags(userId: string, fileName: string, newTags: string[]): void {
  const existingTags = getFileTags(userId, fileName);
  const combinedTags = [...existingTags, ...newTags];
  setFileTags(userId, fileName, combinedTags);
}

/**
 * Remove specific tags from a file
 */
export function removeFileTags(userId: string, fileName: string, tagsToRemove: string[]): void {
  const existingTags = getFileTags(userId, fileName);
  const cleanTagsToRemove = tagsToRemove.map(tag => tag.trim().toLowerCase());
  const remainingTags = existingTags.filter(tag => !cleanTagsToRemove.includes(tag));
  setFileTags(userId, fileName, remainingTags);
}

/**
 * Delete all tags for a file
 */
export function deleteFileTags(userId: string, fileName: string): void {
  setFileTags(userId, fileName, []);
}

/**
 * Get all unique tags used by a user
 */
export function getAllUserTags(userId: string): string[] {
  const userTags = getUserTags(userId);
  const allTags = new Set<string>();
  
  Object.values(userTags).forEach(tags => {
    tags.forEach(tag => allTags.add(tag));
  });
  
  return Array.from(allTags).sort();
}

/**
 * Search files by tag
 */
export function searchFilesByTag(userId: string, searchTag: string): string[] {
  const userTags = getUserTags(userId);
  const cleanSearchTag = searchTag.trim().toLowerCase();
  const matchingFiles: string[] = [];
  
  Object.entries(userTags).forEach(([fileName, tags]) => {
    if (tags.includes(cleanSearchTag)) {
      matchingFiles.push(fileName);
    }
  });
  
  return matchingFiles;
}

/**
 * Search files by multiple tags (AND logic - file must have all tags)
 */
export function searchFilesByTags(userId: string, searchTags: string[]): string[] {
  const userTags = getUserTags(userId);
  const cleanSearchTags = searchTags.map(tag => tag.trim().toLowerCase());
  const matchingFiles: string[] = [];
  
  Object.entries(userTags).forEach(([fileName, tags]) => {
    const hasAllTags = cleanSearchTags.every(searchTag => tags.includes(searchTag));
    if (hasAllTags) {
      matchingFiles.push(fileName);
    }
  });
  
  return matchingFiles;
}

/**
 * Get tag statistics
 */
export function getTagStats(userId: string): { tag: string; count: number }[] {
  const userTags = getUserTags(userId);
  const tagCounts = new Map<string, number>();
  
  Object.values(userTags).forEach(tags => {
    tags.forEach(tag => {
      tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
    });
  });
  
  return Array.from(tagCounts.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Rename a file's tags when file is renamed
 */
export function renameFileTags(userId: string, oldFileName: string, newFileName: string): void {
  const tags = getFileTags(userId, oldFileName);
  if (tags.length > 0) {
    setFileTags(userId, newFileName, tags);
    deleteFileTags(userId, oldFileName);
  }
}

/**
 * Clean up tags for deleted files
 */
export function cleanupDeletedFileTags(userId: string, existingFileNames: string[]): void {
  const userTags = getUserTags(userId);
  const fileNamesSet = new Set(existingFileNames);
  
  Object.keys(userTags).forEach(fileName => {
    if (!fileNamesSet.has(fileName)) {
      deleteFileTags(userId, fileName);
    }
  });
}

/**
 * Export all tags for backup
 */
export function exportTags(): string {
  const allTags = localStorage.getItem(TAGS_STORAGE_KEY);
  return allTags || '{}';
}

/**
 * Import tags from backup
 */
export function importTags(tagsJson: string): boolean {
  try {
    const tagsData = JSON.parse(tagsJson);
    localStorage.setItem(TAGS_STORAGE_KEY, JSON.stringify(tagsData));
    return true;
  } catch (error) {
    console.error('Error importing tags:', error);
    return false;
  }
}
